import Button from './button';
import emailjs from '@emailjs/browser';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

const TIME_OPTIONS = Array.from({ length: 13 }, (_, i) => {
    const hour = i + 8;
    const period = hour < 12 ? 'AM' : 'PM';
    const displayHour = hour > 12 ? hour - 12 : hour;
    return { value: `${String(hour).padStart(2, '0')}:00`, label: `${displayHour}:00 ${period}` };
});

const toDateString = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const formatDisplayDate = (value) => {
    if (!value) return 'Select a date';
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', {
        weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    });
};

const useClickOutside = (ref, onClose, enabled = true) => {
    useEffect(() => {
        if (!enabled) return undefined;
        const handleClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) onClose();
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [ref, onClose, enabled]);
};

const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 639px)').matches);

    useEffect(() => {
        const mq = window.matchMedia('(max-width: 639px)');
        const handleChange = (e) => setIsMobile(e.matches);
        mq.addEventListener('change', handleChange);
        return () => mq.removeEventListener('change', handleChange);
    }, []);

    return isMobile;
};

const BookingMobileDrawer = ({ open, onClose, title, children }) => {
    useEffect(() => {
        if (!open) return undefined;
        document.body.style.overflow = 'hidden';
        const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handleKey);
        return () => {
            document.body.style.overflow = '';
            document.removeEventListener('keydown', handleKey);
        };
    }, [open, onClose]);

    if (!open) return null;

    return createPortal(
        <>
            <button type="button" className="booking-drawer-backdrop" onClick={onClose} aria-label="Close" />
            <div className="booking-drawer" role="dialog" aria-label={title}>
                <div className="booking-drawer-handle" aria-hidden="true" />
                <div className="booking-drawer-header">
                    <h3 className="booking-drawer-title">{title}</h3>
                    <button type="button" className="booking-drawer-close" onClick={onClose}>Done</button>
                </div>
                <div className="booking-drawer-content">{children}</div>
            </div>
        </>,
        document.body
    );
};

const BookingCalendar = ({ value, onChange, onSelect }) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const [viewDate, setViewDate] = useState(() => {
        if (value) {
            const [y, m] = value.split('-').map(Number);
            return new Date(y, m - 1, 1);
        }
        return new Date(today.getFullYear(), today.getMonth(), 1);
    });

    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);

    const selectDay = (day) => {
        const selected = new Date(year, month, day);
        if (selected < today) return;
        onChange(toDateString(selected));
        onSelect?.();
    };

    const prevMonth = () => setViewDate(new Date(year, month - 1, 1));
    const nextMonth = () => setViewDate(new Date(year, month + 1, 1));
    const canGoPrev = new Date(year, month, 1) > new Date(today.getFullYear(), today.getMonth(), 1);

    return (
        <div className="booking-calendar">
            <div className="booking-calendar-header">
                <button type="button" className="booking-calendar-nav" onClick={prevMonth} disabled={!canGoPrev} aria-label="Previous month">
                    ‹
                </button>
                <span className="booking-calendar-title">{MONTHS[month]} {year}</span>
                <button type="button" className="booking-calendar-nav" onClick={nextMonth} aria-label="Next month">
                    ›
                </button>
            </div>
            <div className="booking-calendar-weekdays">
                {WEEKDAYS.map((day) => (
                    <span key={day} className="booking-calendar-weekday">{day}</span>
                ))}
            </div>
            <div className="booking-calendar-grid">
                {cells.map((day, i) => {
                    if (!day) return <span key={`empty-${i}`} />;
                    const dateStr = toDateString(new Date(year, month, day));
                    const isPast = new Date(year, month, day) < today;
                    const isSelected = value === dateStr;
                    return (
                        <button
                            key={day}
                            type="button"
                            className={`booking-calendar-day${isSelected ? ' is-selected' : ''}${isPast ? ' is-disabled' : ''}`}
                            onClick={() => selectDay(day)}
                            disabled={isPast}
                        >
                            {day}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

const BookingTimeList = ({ value, onChange, onSelect }) => (
    <ul className="booking-time-list" role="listbox" aria-label="Choose time">
        {TIME_OPTIONS.map((option) => (
            <li key={option.value} role="option" aria-selected={value === option.value}>
                <button
                    type="button"
                    className={`booking-time-option${value === option.value ? ' is-selected' : ''}`}
                    onClick={() => { onChange(option.value); onSelect?.(); }}
                >
                    {option.label}
                </button>
            </li>
        ))}
    </ul>
);

const BookingDatePicker = ({ value, onChange, name, required }) => {
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);
    const isMobile = useIsMobile();
    const close = () => setOpen(false);

    useClickOutside(containerRef, close, open && !isMobile);

    return (
        <div className="booking-picker" ref={containerRef}>
            <label className="form-label">Select Preferred Call Date</label>
            <input type="hidden" name={name} value={value} required={required} />
            <button
                type="button"
                className="booking-picker-trigger"
                onClick={() => setOpen(true)}
                aria-expanded={open}
                aria-haspopup="dialog"
            >
                <span className={value ? 'text-textBody' : 'text-textPlaceholder'}>
                    {formatDisplayDate(value)}
                </span>
                <svg className="booking-picker-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                </svg>
            </button>
            {isMobile ? (
                <BookingMobileDrawer open={open} onClose={close} title="Select Date">
                    <BookingCalendar value={value} onChange={onChange} onSelect={close} />
                </BookingMobileDrawer>
            ) : open && (
                <div className="booking-picker-dropdown" role="dialog" aria-label="Choose date">
                    <BookingCalendar value={value} onChange={onChange} onSelect={close} />
                </div>
            )}
        </div>
    );
};

const BookingTimeSelect = ({ value, onChange, name, required }) => {
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);
    const isMobile = useIsMobile();
    const selected = TIME_OPTIONS.find((o) => o.value === value);
    const close = () => setOpen(false);

    useClickOutside(containerRef, close, open && !isMobile);

    return (
        <div className="booking-picker" ref={containerRef}>
            <label className="form-label">Preferred Time</label>
            <input type="hidden" name={name} value={value} required={required} />
            <button
                type="button"
                className="booking-picker-trigger"
                onClick={() => setOpen(true)}
                aria-expanded={open}
                aria-haspopup="listbox"
            >
                <span className={value ? 'text-textBody' : 'text-textPlaceholder'}>
                    {selected ? selected.label : 'Select a time'}
                </span>
                <svg className="booking-picker-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
            </button>
            {isMobile ? (
                <BookingMobileDrawer open={open} onClose={close} title="Select Time">
                    <BookingTimeList value={value} onChange={onChange} onSelect={close} />
                </BookingMobileDrawer>
            ) : open && (
                <div className="booking-picker-dropdown">
                    <BookingTimeList value={value} onChange={onChange} onSelect={close} />
                </div>
            )}
        </div>
    );
};

const INDIAN_PHONE_REGEX = /^[6-9]\d{9}$/;

const IndianPhoneInput = ({ value, onChange, id }) => (
    <div className="booking-phone-input">
        <div className="booking-phone-prefix" aria-hidden="true">
            <span className="booking-phone-flag">🇮🇳</span>
            <span className="booking-phone-code">+91</span>
        </div>
        <input
            id={id}
            className="booking-phone-field"
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            placeholder="Enter your contact number"
            value={value}
            onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 10))}
            pattern="[6-9][0-9]{9}"
            required
            maxLength={10}
        />
        <input
            type="hidden"
            name="phone"
            value={INDIAN_PHONE_REGEX.test(value) ? `+91 ${value}` : ''}
            required
        />
    </div>
);

const BookingForm = () => {
    const [preferredDate, setPreferredDate] = useState('');
    const [preferredTime, setPreferredTime] = useState('');
    const [phone, setPhone] = useState('');
    const formRef = React.useRef();
    const [sendStatus, setSendStatus] = React.useState("idle");

    useEffect(() => {
        emailjs.init({ publicKey: process.env.REACT_APP_SELF_EMAILJS_PUBLIC_KEY });
    }, []);

    const handleSendStatus = (status) => {
        setSendStatus(status);
        if (status === 'idle') {
            setPreferredDate('');
            setPreferredTime('');
            setPhone('');
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (sendStatus === 'loading') {
            return;
        }

        handleSendStatus('loading');

        const formData = new FormData(event.target);
        const clientEmail = formData.get('email');
        const templateParams = {
            name: formData.get('name'),
            email: clientEmail,
            reply_to: clientEmail,
            to_email: clientEmail,
            from_email: clientEmail,
            date: formData.get('date'),
            time: formData.get('time'),
            callType: formData.get('callType'),
            calltype: formData.get('callType'),
            message: formData.get('message'),
            phone: formData.get('phone'),
        };

        const replyToField = event.target.querySelector('[name="reply_to"]');
        if (replyToField) {
            replyToField.value = clientEmail;
        }
        const callTypeField = event.target.querySelector('[name="call_type"]');
        if (callTypeField) {
            callTypeField.value = formData.get('callType');
        }
        const timeSlotField = event.target.querySelector('[name="time_slot"]');
        if (timeSlotField) {
            timeSlotField.value = formData.get('time');
        }

        let selfSent = false;

        try {
            await emailjs.sendForm(
                process.env.REACT_APP_SELF_EMAILJS_SERVICE_ID,
                process.env.REACT_APP_SELF_EMAILJS_TEMPLATE_ID,
                event.target
            );
            selfSent = true;
        } catch (err) {
            // Self notification failed
        }

        try {
            await emailjs.send(
                process.env.REACT_APP_CLIENT_EMAILJS_SERVICE_ID,
                process.env.REACT_APP_CLIENT_EMAILJS_TEMPLATE_ID,
                templateParams
            );
        } catch (err) {
            // Client confirmation is non-critical
        }

        if (selfSent) {
            handleSendStatus('success');
        } else {
            handleSendStatus('error');
            toast.error("Failed to send your booking request. Please try again.");
        }
    };

    if (sendStatus === 'success') {
        return (
            <section className="booking-form" id="booking-form">
                <div className="booking-form-outer">
                    <div className="booking-form-container">
                        <div className="form-success">
                            <h2 className="text-brand-800">Thank You!</h2>
                            <p className="text-textBody text-lg">
                                Your discovery session request has been sent successfully.
                            </p>
                            <p className="text-textBody">
                                We'll get back to you within 24 hours to confirm your session. Check your email for a confirmation.
                            </p>
                            <Button type="primary" onClick={() => handleSendStatus('idle')} className="mt-4">
                                Book Another Session
                            </Button>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="booking-form" id="booking-form">
            <ToastContainer />
            <div className="booking-form-outer">
                <form ref={formRef} className="booking-form-container" onSubmit={handleSubmit}>
                    <input type="hidden" name="form-name" value="booking-form" />
                    <input type="hidden" name="call_type" value="" />
                    <input type="hidden" name="time_slot" value="" />
                    <input type="hidden" name="reply_to" value="" />
                    <h2 className="text-brand-800 text-center">Book a Clarity Call</h2>
                    <p className="text-textBody text-center">
                        Take the first step toward a life of healing and love. Fill out the form below to schedule your free discovery session and begin your journey.
                    </p>

                    <div className="flex flex-col gap-1.5">
                        <label htmlFor="booking-name" className="form-label">Your Name</label>
                        <input className="booking-form-input" id="booking-name" name="name" required type="text" placeholder="Enter your name" />
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <label htmlFor="booking-phone" className="form-label">Mobile Number</label>
                        <IndianPhoneInput id="booking-phone" value={phone} onChange={setPhone} />
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <label htmlFor="booking-email" className="form-label">Email Address</label>
                        <input className="booking-form-input" id="booking-email" name="email" required type="email" placeholder="Enter your email" />
                    </div>

                    <div className="booking-form-radio-container">
                        <label className="form-label text-nowrap">Select Type of Call:</label>
                        <div className="options-container">
                            <label className="booking-form-radio-option">
                                <input className="size-4 accent-brand-800" id="zoom-meeting" defaultChecked type="radio" name="callType" value="Zoom Meeting" required />
                                <span className="booking-form-radio-label">Zoom Meeting</span>
                            </label>
                            <label className="booking-form-radio-option">
                                <input className="size-4 accent-brand-800" id="over-mobile" type="radio" name="callType" value="Over Mobile" required />
                                <span className="booking-form-radio-label">Over Mobile</span>
                            </label>
                        </div>
                    </div>

                    <div className="booking-form-datetime-row">
                        <BookingDatePicker
                            name="date"
                            value={preferredDate}
                            onChange={setPreferredDate}
                            required
                        />
                        <BookingTimeSelect
                            name="time"
                            value={preferredTime}
                            onChange={setPreferredTime}
                            required
                        />
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <label htmlFor="booking-message" className="form-label">Your Message (Optional)</label>
                        <textarea className="booking-form-textarea" id="booking-message" name="message" placeholder="Share your goals, challenges, or anything on your mind..." rows="4"></textarea>
                        <span className="text-sm text-brand-700">Your input is confidential and helps both of us to make the most of our session.</span>
                    </div>

                    <Button type="primary" htmlType="submit" className="w-full mt-1">
                        {sendStatus === 'loading' ? 'Sending...' : 'Schedule A Call Now'}
                    </Button>
                </form>
            </div>
        </section>
    );
};

export default BookingForm;
